import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  MapPin, Phone, Mail, Instagram, Music2, 
  Mountain, Waves, TreePine, UtensilsCrossed, 
  ChevronDown, Menu, X, Globe, MessageCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import OptimizedImage from '../components/OptimizedImage';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/1tzx91jj_504423129_17847547452489584_2138028376177138441_n.jpg";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/mbvvkqnb_Gemini_Generated_Image_551ua551ua551ua5.png";
// Tek otaqlı kottec şəkilləri
const SINGLE_ROOM_1 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/hqpzpmmf_002.png";
const SINGLE_ROOM_2 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/wo5vgdul_003.png";
const SINGLE_ROOM_3 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/tjrhnoju_004.png";
// 3 otaqlı kottec şəkilləri
const THREE_ROOM_1 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/gbuaz64o_515362684_17850365115489584_6507284370249302218_n.jpg";
const THREE_ROOM_2 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/cu3bs8v7_screenshot_20260407_160527.jpg";
const THREE_ROOM_3 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/emg6e2is_screenshot_20260407_160542.jpg";
const THREE_ROOM_4 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/rm48p4ph_screenshot_20260407_160555.jpg";
const THREE_ROOM_5 = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/0jz94z4c_screenshot_20260407_160608.jpg";
const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/d1c3ef13-cb1e-4319-a69e-9469e12f07f1/images/e29e727561c63146045731b5a133801d47bfa321a1d80103502ab37fab29f58f.png";
const COTTAGE_LOCATION = "https://customer-assets.emergentagent.com/job_alpine-sanctuary/artifacts/mbvvkqnb_Gemini_Generated_Image_551ua551ua551ua5.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Header Component
const Header = () => {
  const { t, language, setLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t('nav_about') },
    { href: '#cottages', label: t('nav_cottages') },
    { href: '#amenities', label: t('nav_amenities') },
    { href: '#contact', label: t('nav_contact') },
  ];

  const languages = [
    { code: 'az', label: 'AZ' },
    { code: 'en', label: 'EN' },
    { code: 'ru', label: 'RU' },
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'glass-header py-3' : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="#" className="flex items-center gap-3" data-testid="header-logo">
            <img src={LOGO_URL} alt="Oriva Quba" className="h-12 w-auto rounded-lg" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  isScrolled ? 'text-[#1A2415] hover:text-[#D97706]' : 'text-white hover:text-[#D97706]'
                }`}
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                  isScrolled ? 'text-[#1A2415] hover:bg-[#2D5016]/10' : 'text-white hover:bg-white/10'
                }`}
                data-testid="language-switcher-btn"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm font-medium">{language.toUpperCase()}</span>
              </button>
              
              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-24 bg-white rounded-md shadow-lg py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { setLanguage(lang.code); setLangMenuOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F9F8F6] ${
                        language === lang.code ? 'text-[#D97706] font-medium' : 'text-[#1A2415]'
                      }`}
                      data-testid={`lang-${lang.code}`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Link */}
            <Link
              to="/admin"
              className={`hidden sm:block text-sm font-medium tracking-wide transition-colors ${
                isScrolled ? 'text-[#1A2415] hover:text-[#D97706]' : 'text-white hover:text-[#D97706]'
              }`}
              data-testid="nav-admin"
            >
              {t('nav_admin')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className={isScrolled ? 'text-[#1A2415]' : 'text-white'} />
              ) : (
                <Menu className={isScrolled ? 'text-[#1A2415]' : 'text-white'} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.nav 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden mt-4 pb-4 border-t border-white/10"
          >
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-base font-medium ${
                    isScrolled ? 'text-[#1A2415]' : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/admin"
                className={`text-base font-medium ${
                  isScrolled ? 'text-[#1A2415]' : 'text-white'
                }`}
              >
                {t('nav_admin')}
              </Link>
            </div>
          </motion.nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Parallax Background */}
      <motion.div style={{ y }} className="absolute inset-0">
        <OptimizedImage 
          src={HERO_IMAGE}
          alt="Oriva Quba Mountains"
          className="w-full h-[120%]"
          priority={true}
        />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 hero-overlay" />

      {/* Content */}
      <motion.div 
        style={{ opacity }}
        className="relative h-full flex flex-col items-center justify-center text-center px-4"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="overline mb-6"
        >
          Quba, Azerbaijan
        </motion.p>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-4xl sm:text-5xl lg:text-7xl font-medium text-white tracking-tighter leading-none mb-6"
        >
          {t('hero_title')}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-lg sm:text-xl text-white/80 max-w-2xl mb-10 leading-relaxed"
        >
          {t('hero_subtitle')}
        </motion.p>

        <motion.a
          href="#about"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="btn-primary"
          data-testid="hero-cta"
        >
          {t('hero_cta')}
        </motion.a>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown className="w-8 h-8 text-white/60" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Scroll Reveal Section Wrapper
const RevealSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll();
  const imageY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section id="about" className="section-padding bg-[#F9F8F6]" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div variants={fadeInUp} className="relative">
            <motion.div style={{ y: imageY }} className="image-hover-zoom rounded-lg overflow-hidden">
              <OptimizedImage 
                src={COTTAGE_LOCATION}
                alt="Oriva Quba Location"
                className="w-full h-[400px] lg:h-[600px]"
              />
            </motion.div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-[#D97706] rounded-lg -z-10" />
          </motion.div>

          {/* Text */}
          <div className="lg:pl-8">
            <motion.p variants={fadeInUp} className="overline mb-4">{t('about_overline')}</motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A2415] mb-6 tracking-tight">
              {t('about_title')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#4A5D43] text-lg leading-relaxed mb-8">
              {t('about_text')}
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#2D5016]/10 flex items-center justify-center">
                  <Mountain className="w-6 h-6 text-[#2D5016]" />
                </div>
                <span className="text-[#1A2415] font-medium">{t('amenity_mountain')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#2D5016]/10 flex items-center justify-center">
                  <Waves className="w-6 h-6 text-[#2D5016]" />
                </div>
                <span className="text-[#1A2415] font-medium">{t('amenity_river')}</span>
              </div>
            </motion.div>
          </div>
        </RevealSection>
      </div>
    </section>
  );
};

// Cottages Section with Gallery
const CottagesSection = () => {
  const { t } = useLanguage();
  
  const images = [
    { src: HERO_IMAGE },
    { src: SINGLE_ROOM_1 },
    { src: SINGLE_ROOM_2 },
    { src: THREE_ROOM_1 },
    { src: THREE_ROOM_2 },
    { src: THREE_ROOM_3 },
    { src: SINGLE_ROOM_3 },
    { src: THREE_ROOM_4 },
    { src: THREE_ROOM_5 },
  ];

  return (
    <section id="cottages" className="section-padding bg-white" data-testid="cottages-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="overline mb-4">{t('cottages_overline')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A2415] tracking-tight">
              {t('cottages_title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((img, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="image-hover-zoom rounded-lg overflow-hidden aspect-square"
                data-testid={`gallery-image-${index}`}
              >
                <OptimizedImage 
                  src={img.src}
                  alt={`Kottec ${index + 1}`}
                  className="w-full h-full"
                />
              </motion.div>
            ))}
          </div>
        </RevealSection>
      </div>
    </section>
  );
};

// Amenities Section
const AmenitiesSection = () => {
  const { t } = useLanguage();

  const amenities = [
    { icon: Waves, title: t('amenity_river'), desc: t('amenity_river_desc') },
    { icon: Mountain, title: t('amenity_mountain'), desc: t('amenity_mountain_desc') },
    { icon: TreePine, title: t('amenity_playground'), desc: t('amenity_playground_desc') },
    { icon: UtensilsCrossed, title: t('amenity_restaurant'), desc: t('amenity_restaurant_desc') },
  ];

  return (
    <section 
      id="amenities" 
      className="section-padding bg-[#F9F8F6]"
      data-testid="amenities-section"
    >
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="overline mb-4">{t('amenities_overline')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A2415] tracking-tight">
              {t('amenities_title')}
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {amenities.map((amenity, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white p-8 rounded-lg border border-[#E5E3DB] hover:shadow-lg transition-shadow duration-300"
                data-testid={`amenity-card-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#2D5016]/10 flex items-center justify-center mb-6">
                  <amenity.icon className="w-8 h-8 text-[#2D5016]" />
                </div>
                <h3 className="text-xl font-medium text-[#1A2415] mb-3">{amenity.title}</h3>
                <p className="text-[#4A5D43] text-sm leading-relaxed">{amenity.desc}</p>
              </motion.div>
            ))}
          </div>
        </RevealSection>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const { t } = useLanguage();

  const images = [
    { src: HERO_IMAGE },
    { src: SINGLE_ROOM_1 },
    { src: SINGLE_ROOM_2 },
    { src: THREE_ROOM_1 },
    { src: THREE_ROOM_2 },
    { src: THREE_ROOM_3 },
    { src: SINGLE_ROOM_3 },
    { src: THREE_ROOM_4 },
    { src: THREE_ROOM_5 },
  ];

  return (
    <section id="gallery" className="section-padding bg-white" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <p className="overline mb-4">{t('gallery_overline')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1A2415] tracking-tight">
              {t('gallery_title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {images.map((img, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="image-hover-zoom rounded-lg overflow-hidden aspect-square"
                data-testid={`gallery-image-${index}`}
              >
                <img 
                  src={img.src}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            ))}
          </div>
        </RevealSection>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();

  return (
    <section id="contact" className="section-padding bg-[#1A2415]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <RevealSection className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Info */}
          <div>
            <motion.p variants={fadeInUp} className="overline mb-4">{t('contact_overline')}</motion.p>
            <motion.h2 variants={fadeInUp} className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white tracking-tight mb-10">
              {t('contact_title')}
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D97706]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#D97706]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">{t('contact_address')}</p>
                  <p className="text-white text-lg">Quba Qəçrəş yolu</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D97706]/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#D97706]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">{t('contact_phone')}</p>
                  <a href="tel:+994557172525" className="text-white text-lg hover:text-[#D97706] transition-colors">
                    +994 55 717 25 25
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#D97706]/20 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#D97706]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">{t('contact_email')}</p>
                  <a href="mailto:info@oriva.az" className="text-white text-lg hover:text-[#D97706] transition-colors">
                    info@oriva.az
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Social Media */}
            <motion.div variants={fadeInUp} className="mt-10">
              <p className="text-white/60 text-sm mb-4">{t('contact_social')}</p>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/oriva.quba/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D97706] transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram className="w-5 h-5 text-white" />
                </a>
                <a 
                  href="https://www.tiktok.com/@oriva.quba" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D97706] transition-colors"
                  data-testid="social-tiktok"
                >
                  <Music2 className="w-5 h-5 text-white" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Map placeholder */}
          <motion.div variants={fadeInUp} className="relative">
            <div className="w-full h-[400px] lg:h-full rounded-lg overflow-hidden bg-[#2D5016]/20">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d48864.76614899989!2d48.47!3d41.36!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x403eb5c6b0e09c5d%3A0x8d5e5e5e5e5e5e5e!2sQuba%2C%20Azerbaijan!5e0!3m2!1sen!2s!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Oriva Quba Location"
              />
            </div>
          </motion.div>
        </RevealSection>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="bg-[#121A0F] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Oriva Quba" className="h-10 w-auto rounded-lg" />
            <p className="text-white/60 text-sm max-w-xs">{t('footer_description')}</p>
          </div>

          <div className="flex items-center gap-6">
            <a 
              href="https://www.instagram.com/oriva.quba/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#D97706] transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a 
              href="https://www.tiktok.com/@oriva.quba" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/60 hover:text-[#D97706] transition-colors"
            >
              <Music2 className="w-5 h-5" />
            </a>
          </div>
        </div>

        <div className="border-t border-white/10 mt-8 pt-8 text-center">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} Oriva Quba. {t('footer_rights')}.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const WhatsAppButton = () => {
  const phoneNumber = "994557172525";
  const whatsappUrl = `https://wa.me/${phoneNumber}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:bg-[#128C7E] transition-all duration-300 hover:scale-110"
      data-testid="whatsapp-btn"
      aria-label="WhatsApp ilə əlaqə"
    >
      <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </a>
  );
};

// Main Home Page Component
const Home = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <CottagesSection />
        <AmenitiesSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Home;
